import classnames from "classnames";
import React from "react";
import { Link } from "react-router-dom";
import { CircleExclamationIcon, SpinnerIcon } from "../Icons";
import "./button.scss";
import style from "../../scss/export.module.scss";

enum ButtonSize {
    "small",
    "medium",
}

enum ButtonType {
    "primary",
    "secondary",
    "tertiary",
    "danger",
    "success",
    "login",
    "my-account",
    "transparent", // TODO: Rename "outlined"
}

export interface TButton extends React.HTMLAttributes<HTMLButtonElement> {
    /** The content to be displayed inside the button */
    children: React.ReactNode;
    /** Optional icon to display on the left side of the button */
    leftIcon?: JSX.Element;
    /** Optional icon to display on the right side of the button */
    rightIcon?: JSX.Element;
    /** The visual style variant of the button */
    type?: keyof typeof ButtonType;
    /** Whether the button is disabled */
    disabled?: boolean;
    /** Whether to show a loading state */
    loading?: boolean;
    /** Click handler for the button */
    onClick?: (e: React.MouseEvent) => void;
    /** Size variant of the button */
    buttonSize?: keyof typeof ButtonSize | null;
    /** Whether the button should take up full width of container */
    fullWidth?: boolean;
    /** Additional CSS class name(s) for the button */
    buttonClassName?: string;
    /** Number to display in the primary badge */
    badgeCount?: number | null;
    /** Number to display in the secondary badge */
    secondaryBadgeCount?: number | null;
    /** Whether to show an exclamation badge */
    exclamationBadge?: boolean;
    /** Whether the button is on a dark background. This will make the button white and only works for type "tertiary" */
    onDarkBackground?: boolean;
    /** Whether the button should be a submit button */
    submit?: boolean;
    /** Color variant when type is "transparent" */
    transparentColor?: "white" | "brand-blue";
    /** Whether the button should behave as a link */
    link?: boolean;
    /** Destination URL when button is a link */
    linkTo?: string;
    /** Whether the link opens in a new tab */
    externalLink?: boolean;
    /** Whether to replace current history entry when navigating */
    replaceLink?: boolean;
    /** Whether to use React Router for navigation */
    reactRouterLink?: boolean;
    /** Whether to allow custom color variables */
    allowCustomColors?: boolean;
    /** Whether to reverse custom colors (use contrast colors) */
    customColorsReversed?: boolean;
    /** Optional ID for the button element */
    id?: string;
}

export const Button = ({
    children,
    leftIcon,
    rightIcon,
    type = "primary",
    submit = false,
    disabled,
    loading,
    onClick,
    buttonSize = null,
    fullWidth = false,
    buttonClassName,
    badgeCount = null,
    secondaryBadgeCount = null,
    exclamationBadge = false,
    onDarkBackground = false,
    transparentColor = "brand-blue",
    link = false,
    linkTo,
    externalLink = false,
    replaceLink = false,
    reactRouterLink = true,
    allowCustomColors = true,
    customColorsReversed = false,
    id,
    ...props
}: TButton) => {
    const className = classnames(buttonClassName, "c-btn", `c-btn--${type}`, buttonSize && `c-btn--${buttonSize}`, {
        "c-btn--disabled": disabled,
        "c-btn--loading": loading,
        "c-btn--full-width": fullWidth,
        "c-btn--on-dark-bg": onDarkBackground,
        [`c-btn--transparent--${transparentColor}`]: type === "transparent",
        "c-btn--default-custom-colors": !allowCustomColors,
        "c-btn--custom-colors-reversed": allowCustomColors && customColorsReversed,
    });

    const labelClassName = classnames((leftIcon || rightIcon) && `c-btn__label-with-icons`);

    const renderButtonContent = () => {
        return (
            <>
                {leftIcon && (
                    <div className="c-btn__icon" aria-hidden>
                        {leftIcon}
                    </div>
                )}
                <div className={labelClassName}>{children}</div>
                {rightIcon && (
                    <div className="c-btn__icon" aria-hidden>
                        {rightIcon}
                    </div>
                )}
                {badgeCount !== null && <span className="c-btn__badge">{badgeCount}</span>}
                {secondaryBadgeCount !== null && (
                    <span
                        className={classnames("c-btn__badge c-btn__badge--secondary", {
                            "c-btn__badge--secondary--shuved": badgeCount,
                        })}
                    >
                        {secondaryBadgeCount}
                    </span>
                )}
                {exclamationBadge && (
                    <div className="c-btn__exclamation-badge">
                        <CircleExclamationIcon color={style.alertColor} size={24} />
                    </div>
                )}
                {loading && <SpinnerIcon color="#fff" className="c-btn__loading-icon" />}
            </>
        );
    };

    if (link && linkTo) {
        if (externalLink) {
            const formatLink = (link: string) => {
                if (linkTo.startsWith("http://") || linkTo.startsWith("https://")) {
                    return link; // Already valid.
                }

                return `//${link}`;
            };

            return (
                <a href={formatLink(linkTo)} className={className} target="_blank" rel="noopener noreferrer">
                    {renderButtonContent()}
                </a>
            );
        }

        if (!reactRouterLink) {
            return (
                <a href={linkTo} className={className}>
                    {renderButtonContent()}
                </a>
            );
        }

        return (
            <Link to={linkTo} className={className} replace={replaceLink} onClick={e => onClick?.(e)}>
                {renderButtonContent()}
            </Link>
        );
    }

    return (
        <button
            {...props}
            {...(id ? { id: id } : {})}
            type={submit ? "submit" : "button"}
            className={className}
            onClick={e => onClick?.(e)}
            disabled={disabled || loading}
        >
            {renderButtonContent()}
        </button>
    );
};
